// Radar — changelog intelligence. A feed of watched HighLevel features (from the
// platform changelog / whats-new) and, under each, the past tickets it may resolve:
// "a feature dropped → here are the customers who asked for it." Backed by
// /features (FeatureWithMatches[]), /features/ingest, /features/match, PATCH /matches/:id.
import { useEffect, useState } from 'react';
import type {
  FeatureWithMatches,
  FeatureLifecycle,
  FeatureTicketMatch,
} from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, Chip, EmptyNote, ErrorNote, Spinner } from './ui';
import { timeAgo } from '../../lib/time';

const DASH = '—';

// Lifecycle → pill color (matches the ci type-accent palette family).
const LIFECYCLE_META: Record<FeatureLifecycle, { label: string; color: string }> = {
  roadmap: { label: 'Roadmap', color: '#6366f1' },
  labs: { label: 'Labs', color: '#d97706' },
  released: { label: 'Released', color: '#16a34a' },
  known_issue: { label: 'Known issue', color: '#dc2626' },
};

// See lib/time.ts — this was a private copy that parsed zone-less D1 timestamps as local time.
const fmtRelative = (iso: string | null) => timeAgo(iso, { never: DASH });

export default function Radar() {
  const [items, setItems] = useState<FeatureWithMatches[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'ingest' | 'match'>(null);
  const [note, setNote] = useState<string | null>(null);

  function load() {
    setError(null);
    agencyApi
      .features()
      .then(setItems)
      .catch((e: Error) => setError(e.message));
  }

  useEffect(() => {
    load();
  }, []);

  async function ingest() {
    setBusy('ingest');
    setNote(null);
    setError(null);
    try {
      const r = await agencyApi.ingestFeatures();
      setNote(r.ingested != null ? `Ingested ${r.ingested} feature${r.ingested === 1 ? '' : 's'}.` : 'Ingest complete.');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function match() {
    setBusy('match');
    setNote(null);
    setError(null);
    try {
      const r = await agencyApi.matchFeatures();
      setNote(r.matched != null ? `Found ${r.matched} ticket match${r.matched === 1 ? '' : 'es'}.` : 'Matching complete.');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  // Optimistically update a match's status after Dismiss / Mark notified.
  function setMatchStatus(featureId: string, matchId: string, status: FeatureTicketMatch['status']) {
    setItems((prev) =>
      prev
        ? prev.map((it) =>
            it.feature.id === featureId
              ? { ...it, matches: it.matches.map((m) => (m.id === matchId ? { ...m, status } : m)) }
              : it,
          )
        : prev,
    );
  }

  async function act(featureId: string, match: FeatureTicketMatch, status: 'dismissed' | 'notified') {
    const prev = match.status;
    setMatchStatus(featureId, match.id, status);
    try {
      await agencyApi.patchMatch(match.id, status);
    } catch (e) {
      setMatchStatus(featureId, match.id, prev); // revert on failure
      setError((e as Error).message);
    }
  }

  return (
    <div className="space-y-5">
      <Card className="border-brand/20 bg-brand/5 p-4 text-sm text-slate-600">
        <span className="font-medium text-slate-800">Signals</span> watches the HighLevel changelog. When
        a feature ships, it scores past tickets against it — so you can circle back to the customers who
        asked. <span className="font-medium">Ingest</span> pulls the latest features;{' '}
        <span className="font-medium">Match</span> re-scores tickets against released features.
      </Card>

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={ingest} disabled={busy !== null}>
          {busy === 'ingest' ? 'Ingesting…' : '↓ Ingest features'}
        </Button>
        <Button variant="secondary" onClick={match} disabled={busy !== null}>
          {busy === 'match' ? 'Matching…' : '⚡ Match tickets'}
        </Button>
        {note && <span className="text-xs text-emerald-600">{note}</span>}
        <span className="ml-auto text-sm text-slate-400">
          {items ? `${items.length} feature${items.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {error && <ErrorNote error={error} />}
      {!items && !error && <Spinner label="Loading feature radar…" />}
      {items && items.length === 0 && (
        <EmptyNote>
          Nothing on the radar yet. Click <span className="font-medium">Ingest features</span> to pull
          the latest changelog.
        </EmptyNote>
      )}

      {items && items.length > 0 && (
        <div className="space-y-4">
          {items.map((it) => (
            <FeatureCard
              key={it.feature.id}
              item={it}
              onAct={(m, status) => act(it.feature.id, m, status)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FeatureCard({
  item,
  onAct,
}: {
  item: FeatureWithMatches;
  onAct: (m: FeatureTicketMatch, status: 'dismissed' | 'notified') => void;
}) {
  const { feature, matches } = item;
  const lc = LIFECYCLE_META[feature.lifecycle] ?? { label: feature.lifecycle, color: '#64748b' };
  const active = matches.filter((m) => m.status === 'suggested');

  return (
    <Card className="overflow-hidden">
      {/* Feature header */}
      <div className="border-b border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">{feature.title}</span>
          <Chip label={lc.label} color={lc.color} />
          {feature.feature_group && <Chip label={feature.feature_group} muted />}
          {feature.confidence && (
            <span className="text-xs text-slate-400">confidence {feature.confidence}</span>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-400">
            {feature.released_at && <span>{fmtRelative(feature.released_at)}</span>}
            {feature.source_url && (
              <a
                href={feature.source_url}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:underline"
              >
                Source ↗
              </a>
            )}
          </div>
        </div>
        {feature.summary && <p className="mt-2 text-sm text-slate-600">{feature.summary}</p>}
      </div>

      {/* Matched tickets */}
      <div className="p-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
          Matched tickets {matches.length > 0 && <span className="text-slate-400">· {matches.length}</span>}
        </div>
        {matches.length === 0 ? (
          <p className="text-sm text-slate-400">
            No ticket matches yet. Run <span className="font-medium">Match tickets</span>.
          </p>
        ) : active.length === 0 ? (
          <p className="text-sm text-slate-400">All matches handled.</p>
        ) : (
          <ul className="space-y-2.5">
            {active.map((m) => (
              <MatchRow key={m.id} match={m} onAct={onAct} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function MatchRow({
  match: m,
  onAct,
}: {
  match: FeatureTicketMatch;
  onAct: (m: FeatureTicketMatch, status: 'dismissed' | 'notified') => void;
}) {
  const t = m.ticket;
  return (
    <li className="flex flex-wrap items-start gap-3 rounded-lg bg-slate-50 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-800">
            {t?.subject ?? `Ticket ${m.ticket_id}`}
          </span>
          {t?.status && <Chip label={t.status} muted />}
          <span className="text-xs text-slate-400">score {m.score}</span>
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {t?.requester_name ? <span>{t.requester_name}</span> : null}
          {t?.created_at ? <span className="text-slate-400"> · {fmtRelative(t.created_at)}</span> : null}
        </div>
        {m.reason && (
          <div className="mt-1 text-xs text-slate-500">
            <span className="font-medium text-slate-600">Why:</span> {m.reason}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="secondary" onClick={() => onAct(m, 'notified')}>
          Mark notified
        </Button>
        <Button variant="ghost" onClick={() => onAct(m, 'dismissed')}>
          Dismiss
        </Button>
      </div>
    </li>
  );
}
